export const SOI_DAO_ROOM_IDS = ['room1', 'room2', 'room3'];

export const SOI_DAO_STATUS_LABELS = {
  pending: { text: 'รอผล', className: 'text-amber-600 bg-amber-50' },
  won: { text: 'ถูกรางวัล', className: 'text-green-700 bg-green-50' },
  lost: { text: 'ไม่ถูก', className: 'text-gray-500 bg-gray-100' },
  settled: { text: 'จบรอบ', className: 'text-gray-600 bg-gray-100' },
  available: { text: 'ว่าง', className: 'text-brand-red bg-red-50' },
  ready: { text: 'พร้อมจ่าย', className: 'text-brand-gold bg-brand-gold-light/40' },
  accumulating: {
    text: 'กำลังสะสม',
    className: 'text-gray-500 bg-gray-100',
  },
  claimed: { text: 'ออกแล้ว', className: 'text-green-700 bg-green-50' },
  locked: { text: 'กำลังสะสม', className: 'text-gray-500 bg-gray-100' },
  unclaimed: {
    text: 'พร้อมจ่าย',
    className: 'text-brand-gold bg-brand-gold-light/40',
  },
};

export function closeAtToRemaining(closeAt) {
  if (!closeAt) return null;
  const ms = new Date(closeAt).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true };
  }
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  return { days, hours, minutes, seconds, isExpired: false };
}

export function createSoiDaoIdempotencyKey() {
  const raw =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `sd-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  return String(raw).slice(0, 64);
}

export function normalizeCell(cell) {
  if (!cell) return { position: '00', state: 'available' };
  const position = String(
    cell.position ?? cell.starNumber ?? cell.starPosition ?? '',
  ).padStart(2, '0');
  if (cell.state === 'won' || cell.state === 'lost') {
    return {
      position,
      state: cell.state,
      result: cell.result,
      payout: Number(cell.payout) || 0,
    };
  }
  if (cell.state === 'settled') {
    return {
      position,
      state: cell.outcome === 'won' ? 'won' : 'lost',
      result: cell.result,
      payout: Number(cell.payout) || 0,
    };
  }
  if (cell.state === 'pending') {
    return { position, state: 'pending' };
  }
  return { position, state: 'available' };
}

export function normalizePrize(prize, index = 0, fallbackMilestone) {
  const amount = Array.isArray(prize)
    ? Number(prize)
    : Number(prize?.amount ?? prize) || 0;
  if (typeof prize !== 'object' || prize == null) {
    return {
      slot: index + 1,
      amount,
      status: 'accumulating',
      milestone: fallbackMilestone,
      winningNumber: undefined,
      starPosition: undefined,
      payout: undefined,
      entryCount: 0,
    };
  }

  let status = prize.status || 'accumulating';
  if (status === 'locked') status = 'accumulating';
  if (status === 'unclaimed') status = 'ready';

  const claimed = status === 'claimed';
  return {
    slot: prize.slot ?? index + 1,
    amount,
    status,
    milestone: prize.milestone ?? fallbackMilestone,
    winningNumber: prize.winningNumber,
    starPosition: claimed
      ? prize.starPosition ?? prize.starNumber
      : undefined,
    payout: claimed ? Number(prize.payout ?? amount) || amount : undefined,
    entryCount: Number(prize.entryCount) || 0,
  };
}

export function normalizeRoundPayload(data) {
  const rawCells = data.cells || data.board || [];
  const cells = Array.from({ length: 100 }, (_, index) => {
    const position = String(index).padStart(2, '0');
    const found =
      rawCells.find(
        (cell) =>
          String(cell.position ?? cell.starNumber ?? '').padStart(2, '0') ===
          position,
      ) || { position, state: 'available' };
    return normalizeCell(found);
  });

  const roomPrizes = data.room?.prizes || [];
  const prizesSource =
    Array.isArray(data.prizes) && data.prizes.length
      ? data.prizes
      : roomPrizes;
  const milestones = data.room?.milestones || [];

  return {
    ...data,
    cells,
    prizes: prizesSource.map((prize, index) =>
      normalizePrize(prize, index, milestones[index]),
    ),
    remainingTime:
      data.remainingTime || closeAtToRemaining(data.round?.closeAt),
  };
}

export function formatSoiDaoCountdown(remainingTime) {
  if (!remainingTime) return '--:--:--';
  const { days, hours, minutes, seconds } = remainingTime;
  if (days > 0) {
    return `${days}ว ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function tickRemainingTime(prev) {
  if (!prev || prev.isExpired) return prev;
  let { days, hours, minutes, seconds } = prev;
  if (seconds > 0) seconds -= 1;
  else if (minutes > 0) {
    minutes -= 1;
    seconds = 59;
  } else if (hours > 0) {
    hours -= 1;
    minutes = 59;
    seconds = 59;
  } else if (days > 0) {
    days -= 1;
    hours = 23;
    minutes = 59;
    seconds = 59;
  } else {
    return { ...prev, isExpired: true };
  }
  return { days, hours, minutes, seconds, isExpired: false };
}

export function parseSoiDaoLockResult(lock) {
  if (!lock) return [];
  if (Array.isArray(lock.results)) return lock.results;
  if (!lock.result) return [];
  try {
    const parsed = JSON.parse(lock.result);
    return Array.isArray(parsed) ? parsed : [String(lock.result)];
  } catch {
    return String(lock.result)
      .split(/[,\s]+/)
      .map((part) => part.trim())
      .filter(Boolean);
  }
}

export function roomLabel(roomId, rooms = []) {
  const found = rooms.find((room) => room.id === roomId);
  return found?.name || roomId || '-';
}

export function roundStatusLabel(status) {
  if (status === 'activated') return 'เปิดรับ';
  if (status === 'completed') return 'จบรอบ';
  if (status === 'open') return 'กำลังเปิด';
  return status || '-';
}
